import React, { useEffect, useState } from 'react';
import { WifiSlash, CloudArrowUp, CheckCircle, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  peek, subscribe, drain, count as queueCount,
  getLastSyncedAt,
} from '../lib/offlineQueue';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating chip shown only when there's something to say:
 *   • "Offline · N pending"  — navigator is offline
 *   • "Syncing N"            — drain in progress
 *   • "N pending sync"       — online but queue not empty (awaiting retry)
 *
 * Includes a "Retry now" button + a "Last synced Xm ago" footer so drivers /
 * pickers have clear feedback the queue is healthy.
 */
function formatAgo(ts) {
  if (!ts) return null;
  const diff = Math.max(0, Date.now() - ts);
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export default function OfflineIndicator() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [pending, setPending] = useState(queueCount());
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(getLastSyncedAt());
  const [, setTick] = useState(0);

  const doDrain = async (userTriggered = false) => {
    if (syncing || peek().length === 0) return;
    setSyncing(true);
    try {
      const r = await drain({ baseUrl: API });
      setLastSyncedAt(getLastSyncedAt());
      if (r.drained > 0) {
        toast.success(`Synced ${r.drained} offline entr${r.drained !== 1 ? 'ies' : 'y'}`);
      } else if (userTriggered && r.remaining > 0) {
        toast.warning('Still offline — nothing could be synced just yet.');
      }
      if (r.failed > 0) {
        toast.warning(`${r.failed} entr${r.failed !== 1 ? 'ies' : 'y'} rejected by the server — please check.`);
      }
    } catch (_) {
      /* ignore */
    } finally {
      setSyncing(false);
      setPending(queueCount());
    }
  };

  useEffect(() => {
    const onOnline = () => { setOnline(true); doDrain(false); };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const unsub = subscribe((list) => setPending(list.length));
    // Re-render every minute so the "Xm ago" label stays fresh
    const clockT = setInterval(() => setTick((n) => n + 1), 60 * 1000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      unsub();
      clearInterval(clockT);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic retry while online and pending
  useEffect(() => {
    if (!online || pending === 0 || syncing) return;
    const t = setTimeout(() => doDrain(false), 15000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, pending, syncing]);

  if (online && pending === 0) return null;

  let label, tone, Icon;
  if (!online) {
    label = pending > 0 ? `Offline · ${pending} pending` : 'Offline';
    tone = 'bg-amber-500 text-white border-amber-700';
    Icon = WifiSlash;
  } else if (syncing) {
    label = `Syncing ${pending}`;
    tone = 'bg-brand text-white border-brand';
    Icon = CloudArrowUp;
  } else {
    label = `${pending} pending sync`;
    tone = 'bg-amber-100 text-amber-900 border-amber-400';
    Icon = CloudArrowUp;
  }

  const ago = formatAgo(lastSyncedAt);

  return (
    <div
      data-testid="offline-indicator"
      data-state={!online ? 'offline' : syncing ? 'syncing' : 'pending'}
      className={`fixed z-[90] left-3 top-[4.5rem] md:top-4 md:left-auto md:right-6 flex flex-col items-stretch min-w-[180px] border-2 shadow-lg ${tone}`}
    >
      <div className="flex items-center gap-2 px-3 py-1.5 font-bold uppercase tracking-widest text-[11px]">
        <Icon size={14} weight="fill" className={syncing ? 'animate-pulse' : ''} />
        <span className="flex-1">{label}</span>
        {online && pending === 0 && <CheckCircle size={12} weight="fill" />}
        {pending > 0 && (
          <button
            type="button"
            onClick={() => doDrain(true)}
            disabled={syncing}
            data-testid="offline-retry-now"
            title="Retry sync now"
            className={`p-1 transition ${syncing ? 'opacity-50' : 'hover:bg-black/10'}`}
          >
            <ArrowsClockwise size={12} weight="bold" className={syncing ? 'animate-spin' : ''} />
          </button>
        )}
      </div>
      {ago && (
        <div className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest border-t ${!online || syncing ? 'bg-black/10 border-black/20' : 'bg-white/50 border-amber-300'}`}>
          Last synced {ago}
        </div>
      )}
    </div>
  );
}
